import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import type { DetailsDrawerEntity } from '@talos/kyoko';
import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  ControlPrefix,
  CustomerOrder,
  Divider,
  Flex,
  FormControlSizes,
  LookbackOption,
  MixpanelEvent,
  Order,
  Panel,
  SearchSelect,
  isDetailsDrawerEntity,
  useMixpanel,
  usePortal,
  type SearchSelectProps,
} from '@talos/kyoko';
import type { RowDoubleClickedEvent } from 'ag-grid-community';
import { noop } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCustomerAssetTransactionsAccess, useFeatureFlag } from '../../../hooks';
import { getOrderDetailsRouteFn } from '../../Routes/routes';
import { AutoCompleteDropdownGroupHeader } from './AutoCompleteDropdownGroupHeader';
import { MonitoringBlotter } from './MonitoringBlotter';
import { NewTabEntitySelector } from './NewTabEntitySelector';
import {
  MONITORING_BLOTTER_PREFIX,
  entityByRequestName,
  entityDisplayNames,
  type BlotterTableEntity,
  type EntityName,
  type MonitoringBlotterTabProps,
} from './types';

export function SingleMonitoringBlotter({
  allowEntityChange,
  entity,
  panelLabel,
  panelId,
  onEntityChange,
}: {
  allowEntityChange: boolean;
  entity: EntityName;
  panelLabel: string;
  panelId: string;
  onEntityChange: (entityName: EntityName | undefined) => void;
}) {
  const mixpanel = useMixpanel();
  const { canViewCustomerAssetTransactions } = useCustomerAssetTransactionsAccess();
  const { openDetailsDrawer } = useDetailsDrawer();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);
  const params = useParams<{ layoutId: string }>();
  const { enableMonitoringSpaces } = useFeatureFlag();
  const getMonitoringOrderDetailsRoute = getOrderDetailsRouteFn('monitoring', enableMonitoringSpaces);

  const getDetailsHref = useCallback(
    (entity: DetailsDrawerEntity) => {
      if (entity instanceof Order) {
        return getMonitoringOrderDetailsRoute({ orderID: entity.OrderID, type: 'order', layoutId: params.layoutId });
      }
      if (entity instanceof CustomerOrder) {
        return getMonitoringOrderDetailsRoute({
          orderID: entity.OrderID,
          type: 'customer-order',
          layoutId: params.layoutId,
        });
      }
    },
    [params.layoutId, getMonitoringOrderDetailsRoute]
  );

  const handleDoubleClickRow = useCallback(
    ({ data }: RowDoubleClickedEvent<BlotterTableEntity>) => {
      if (!data || !isDetailsDrawerEntity(data)) {
        return;
      }
      openDetailsDrawer({ entity: data, getDetailsHref });
    },
    [openDetailsDrawer, getDetailsHref]
  );

  const newTabDefaults = useMemo(() => {
    return {
      defaultColumns: [],
      defaultFilter: {
        _start: LookbackOption.Past24Hours,
        HideAPICalls: true,
      },
      closable: true,
      reorderable: true,
      editable: true,
    } satisfies Partial<MonitoringBlotterTabProps>;
  }, []);

  const { enableBuyingPowerMonitoringBlotter, enableNotifications } = useFeatureFlag();
  const allowedEntityOptions = useMemo(() => {
    const names = Object.keys(entityByRequestName) as EntityName[];
    const allowedNames = names.filter(name => {
      if (name === 'BuyingPower') {
        return enableBuyingPowerMonitoringBlotter;
      }
      if (name === 'Notification') {
        return enableNotifications;
      }
      if (name === 'CustomerAssetTransaction') {
        return canViewCustomerAssetTransactions;
      }

      // Can add more clauses here as in the future

      return true;
    });
    return allowedNames;
  }, [enableBuyingPowerMonitoringBlotter, enableNotifications, canViewCustomerAssetTransactions]);

  const entitySelectProps = useMemo(
    () =>
      ({
        options: allowedEntityOptions,
        getLabel: name => entityDisplayNames[name].name,
        onChange: onEntityChange,
        size: FormControlSizes.Small,
        getGroup: name => entityDisplayNames[name].group,
        dropdownWidth: '240px',
        RenderGroupHeader: AutoCompleteDropdownGroupHeader,
        dropdownPlacement: 'right-end',
      }) satisfies Partial<SearchSelectProps<EntityName>>,
    [onEntityChange, allowedEntityOptions]
  );

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  const [showNewTabDialog, setShowNewTabDialog] = useState(false);

  const handleSetSelectedEntity = useCallback(
    (entityName?: EntityName) => {
      if (entityName) {
        mixpanel.track(MixpanelEvent.ChangeMonitoringBlotterEntity);
        onEntityChange(entityName);
        setInitialFiltersOpen(true);
        setShowNewTabDialog(false);
      }
    },
    [onEntityChange, mixpanel]
  );

  return (
    <>
      {entity && (
        <Panel>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            flex="1 1 auto"
            p="spacing8"
            borderBottom="1px solid var(--backgroundBody)"
          >
            <Box>{panelLabel}</Box>
            <Flex flex="0 0 auto" gap="spacing4">
              {allowEntityChange && (
                <>
                  <SearchSelect
                    selection={entity}
                    {...entitySelectProps}
                    portalize={true}
                    dropdownPlacement="bottom"
                    maxHeight={300}
                    prefix={<ControlPrefix>Entity</ControlPrefix>}
                    initialSortByLabel={false}
                  />
                  <Divider orientation="vertical" />
                </>
              )}
              <Box ref={filtersContainerRef} />
            </Flex>
          </Flex>
          <AccordionGroup>
            <MonitoringBlotter
              onRowDoubleClicked={handleDoubleClickRow}
              blotterID={`${MONITORING_BLOTTER_PREFIX}/${panelId}`}
              tabLabel={panelLabel}
              selectedEntity={entity}
              defaultColumns={newTabDefaults.defaultColumns}
              defaultFilter={newTabDefaults.defaultFilter}
              onCloneTab={noop}
              initialIsOpen={initialFiltersOpen}
              generateOrderDetailsRoute={getMonitoringOrderDetailsRoute}
              source="monitoring"
              layoutId={params.layoutId}
            />
          </AccordionGroup>
        </Panel>
      )}
      {showNewTabDialog && (
        <NewTabEntitySelector
          setSelectedEntity={handleSetSelectedEntity}
          isOpen={showNewTabDialog}
          source="monitoring"
        />
      )}
    </>
  );
}
